import {
  CodexCategorySchema,
  CodexEntrySchema,
  CodexFieldType,
  NovelActSchema,
  NovelChapterSchema,
  NovelSceneSchema,
  NovelSchema,
  NovelSeriesSchema,
  SnippetSchema,
} from '#/index';
import {
  CodexDetail,
  NovelLabel,
  OutlineTree,
  PointOfView,
} from '../../types';
import { codexEntriesToXml, getNovelActTree, getTreeDataById, parseOutlineToXML, parseSceneToXML } from '../../utils';
import { FormatterRenderer } from '..';
import { convertJSONContentToFormattedText } from '@/utils';

type XmlRenderer<T> = FormatterRenderer<T>;

const escapeXml = (text: string | undefined): string =>
  (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildAttrs = (attrs: Record<string, string | undefined>): string =>
  Object.entries(attrs)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${key}="${escapeXml(value)}"`)
    .join(' ');


export const renderNovelXml: XmlRenderer<NovelSchema> = novel =>
  `<novel ${buildAttrs({
    title: novel.attributes.title,
    author: novel.attributes.author,
    language: novel.attributes.language,
    tense: novel.attributes.tense,
  })} />`;

export const renderSeriesXml: XmlRenderer<NovelSeriesSchema> = series =>
  `<series ${buildAttrs({ id: series.id, title: series.attributes.title })}>${escapeXml(
    series.attributes.description
  )}</series>`;

export const renderActXml: XmlRenderer<NovelActSchema> = (act, ctx) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { actIds: [act.id] })
  return parseOutlineToXML(tree, ctx, 'novel', 'content');
};

export const renderChapterXml: XmlRenderer<NovelChapterSchema> = (chapter, ctx) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { chapterIds: [chapter.id] })
  return parseOutlineToXML(tree, ctx, 'novel', 'content');
};

export const renderSceneXml: XmlRenderer<NovelSceneSchema> = (scene, ctx) => {
  const treeData = getTreeDataById(ctx, scene.id)
  if (treeData) {
    return parseSceneToXML(treeData, ctx, 'content');
  }
};


export const renderCodexEntryXml: XmlRenderer<CodexEntrySchema> = (entry, ctx) => codexEntriesToXml(ctx, [entry]).join('');

export const renderCodexCategoryXml: XmlRenderer<CodexCategorySchema> = category =>
  `<codexCategory ${buildAttrs({ name: category.attributes.name })} />`;

export const renderCodexDetailXml: XmlRenderer<CodexDetail> = (detail) => {
  const entry = detail.entry
  const fields = detail.fields
  let attributes = { name: entry.attributes.name } as Record<string, string>
  fields.forEach(d => {
    if (d.reference.attributes.type === CodexFieldType.Text && d.value) {
      attributes[d.name] = d.value
    }
  })

  let description = '';
  const contentFields = fields.filter(d => d.reference.attributes.type !== CodexFieldType.Text && d.value)
  if (contentFields.length === 1) {
    description = contentFields[0].value
  } else {
    contentFields.forEach(d => {
      description += `\n<${d.name}>${d.value}</${d.name}>`
    })
    if (description) {
      description += '\n'
    }
  }

  let after = (description ? `>${description}</${entry.attributes.type}>` : '/>')


  return `<${entry.attributes.type} ${buildAttrs(attributes)} ${after}`
}

export const renderSnippetXml: XmlRenderer<SnippetSchema> = snippet =>
  `<snippet ${buildAttrs({ id: snippet.id, title: snippet.attributes.title })}>${escapeXml(
    snippet.attributes.content ? convertJSONContentToFormattedText(snippet.attributes.content) : ''
  )}</snippet>`;

export const renderOutlineTreeXml: XmlRenderer<OutlineTree> = (outline, ctx) =>
  parseOutlineToXML(outline.data, ctx, outline.rootName, outline.mode);

export const renderLabelXml: XmlRenderer<NovelLabel> = label =>
  `<label ${buildAttrs({
    name: label.data.label,
  })} />`;

export const renderPointOfViewXml: XmlRenderer<PointOfView> = (pov, ctx) => {
  const { codexEntries } = ctx.globalContext.generateStore.getState();
  const character = pov.characterId ? codexEntries.find(entry => entry.id === pov.characterId) : undefined;
  return `<pointOfView ${buildAttrs({ type: pov.value, character: character?.attributes.name })} />`
}





export const xmlRenderers = {
  renderNovelXml,
  renderSeriesXml,
  renderActXml,
  renderChapterXml,
  renderSceneXml,
  renderCodexEntryXml,
  renderCodexCategoryXml,
  renderSnippetXml,
  renderOutlineTreeXml,
  renderLabelXml,
  renderPointOfViewXml,
  renderCodexDetailXml,
}